import React, { useState } from "react";
import api from "../../lib/api";
import { useToast, toastErrorMessage } from "../../lib/toast";
import { Download } from "lucide-react";
import BackupRestorePanel from "../../components/BackupRestorePanel";

const SPECS = {
  companies: {
    label: "Companies",
    endpoint: "/admin/import/companies",
    required: ["company_name", "state", "customer_tier"],
    max: 2000,
    columns: ["company_name","Summary on Company","Company Phone Number","website","street","suburb","postcode","state","major_town","area_state","country","generic_email","current_customer","owns_equipment","customer_tier","tier_reason_notes","type_of_contact","product_segments","mixer_market_segment","mixer_history_need","drill_market_segment","drill_history_need","drill_hire","assigned_rep_email"],
    sample: [
      ["Example Concrete Co","Tier-1 Melbourne customer, primarily maxi-bowls","+61395551234","example.com.au","50 Example St","Richmond","3121","VIC","Melbourne","MELS100","Australia","ops@example.com.au","Yes","Yes","1","Largest mixer fleet in VIC; renewals every 3 yrs.","Customer","M;D","CCOM","Maxi Bowl;Spare Parts","","","","justin@cescoequipment.com.au"],
      ["Sample Drilling Ltd","Considering switch from competitor","+61732551234","sample-drilling.com.au","12 Sample Rd","Eagle Farm","4009","QLD","Brisbane","BNES200","Australia","","No","No","3","Lost the Bates contract, deferring capex 12 months.","Customer","D;G","","","DCON","CFA;Tooling;Wear Parts","HV;HD",""],
    ],
  },
  contacts: {
    label: "Contacts",
    endpoint: "/admin/import/contacts",
    required: ["company_name", "first_name", "last_name"],
    max: 5000,
    columns: ["company_name","Summary on Customer","contact_title","first_name","last_name","position","direct_email","phone_office","phone_mobile","linkedin_url","facebook_url","decision_maker"],
    sample: [
      ["Example Concrete Co","Prefers Tuesday-morning visits. Coffee drinker.","Mr","Paul","Harrison","Operations Manager","paul.harrison@example.com.au","+61395551235","+61412345678","https://linkedin.com/in/paulharrison","https://facebook.com/paul.harrison","Yes"],
      ["Sample Drilling Ltd","Influencer only, not sign-off authority.","Ms","Sarah","Nguyen","Procurement Lead","sarah.n@sample-drilling.com.au","","+61412345679","","","No"],
    ],
  },
  equipment: {
    label: "Equipment",
    endpoint: "/admin/import/equipment",
    required: ["company_name", "item"],
    max: 5000,
    columns: ["company_name","item","manufacturer","model","specific_features","date_of_manufacture","serial_number","type","Cesco Serial Number","product_segment"],
    sample: [
      ["Example Concrete Co","Maxi Bowl Mixer","Cesco","MX-9000","9m³ capacity, hydraulic drive","03/2021","CE-9000-4421","Mixer","PO-2021-44","M"],
      ["Sample Drilling Ltd","Drill Rig","Bauer","BG28","Attachment-based","07/2018","BG28-88213","Drill","REF-4821","D"],
    ],
  },
};

function toCSV(rows) {
  return rows.map(r => r.map(c => {
    const s = String(c ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
}

function downloadCSV(filename, rows) {
  const blob = new Blob([toCSV(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminImport() {
  const [kind, setKind] = useState("companies");
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("skip");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const toast = useToast();
  const spec = SPECS[kind];

  async function upload() {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post(spec.endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const imp = data.imported || 0, upd = data.updated || 0, skp = data.skipped || 0, errs = data.errors?.length || 0;
      const parts = [];
      if (imp)  parts.push(`${imp} created`);
      if (upd)  parts.push(`${upd} updated`);
      if (skp)  parts.push(`${skp} skipped`);
      if (errs) parts.push(`${errs} error${errs === 1 ? "" : "s"}`);
      const msg = parts.length ? parts.join(" · ") : "Nothing changed";
      if (errs) toast.warning(msg);
      else      toast.success(msg);
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    } finally { setBusy(false); }
  }

  function downloadBlankTemplate() {
    const rows = [spec.columns];
    downloadCSV(`cesco_crm_${kind}_template.csv`, rows);
  }
  function downloadSampleTemplate() {
    const rows = [spec.columns, ...spec.sample];
    downloadCSV(`cesco_crm_${kind}_sample.csv`, rows);
  }

  function downloadErrors() {
    if (!result?.errors?.length) return;
    const rows = [["row","error"], ...result.errors.map(e => [e.row, e.error])];
    downloadCSV(`${kind}_import_errors.csv`, rows);
  }

  // Stream the CSV from the backend so we get *every* row, not just what's
  // currently visible in the UI. Same column schema as the import template,
  // so reps can export → edit in Excel → re-import without re-mapping anything.
  async function downloadExport() {
    try {
      const params = kind === "companies" && includeArchived ? { include_archived: true } : {};
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob", params });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `cesco-${kind}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported all ${spec.label.toLowerCase()}`);
    } catch(e) {
      toast.error(toastErrorMessage(e));
    }
  }

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-import-page">
      <div>
        <h1 className="text-2xl font-bold">Bulk CSV Import</h1>
        <p className="text-sm text-gray-500">
          Download a template, fill it in, and upload it below.
          Import order matters: <b>Companies first</b>, then Contacts and Equipment.
        </p>
      </div>

      <BackupRestorePanel />

      <div className="cesco-card p-4">
        <label className="cesco-label">Import type</label>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(SPECS).map(([k, v]) => (
            <button key={k} onClick={()=>{setKind(k); setResult(null); setFile(null);}}
              className={`pill cursor-pointer ${k===kind?"pill-green":""}`}
              style={k!==kind ? {border:"1px dashed #B6BAC5", color:"#666", background:"#fff"} : {}}
              data-testid={`import-kind-${k}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="cesco-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div>
            <div className="font-semibold">1. Download the template</div>
            <div className="text-xs text-gray-500">Pick blank (headers only), sample (headers + 2 example rows), or export everything currently in the CRM.</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="cesco-btn-ghost flex items-center gap-2" onClick={downloadBlankTemplate}
                    data-testid="btn-download-blank">
              <Download size={14}/> Blank
            </button>
            <button className="cesco-btn-ghost flex items-center gap-2" onClick={downloadSampleTemplate}
                    data-testid="btn-download-sample">
              <Download size={14}/> With sample
            </button>
            <button className="cesco-btn-primary flex items-center gap-2" onClick={downloadExport}
                    data-testid="btn-export-all">
              <Download size={14}/> Export all {spec.label.toLowerCase()}
            </button>
          </div>
        </div>
        {kind === "companies" && (
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer" data-testid="include-archived-toggle-wrap">
            <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)}
                   data-testid="include-archived-toggle"/>
            Include archived companies in the export
          </label>
        )}
      </div>

      <div className="cesco-card p-4 space-y-3">
        <div className="font-semibold">2. Rules for filling it in</div>
        <div className="text-sm text-gray-600 space-y-1">
          <div><b>Max rows:</b> {spec.max.toLocaleString()}  ·  <b>Max size:</b> 5 MB  ·  Encoding: UTF-8</div>
          <div><b>Required columns:</b> {spec.required.join(", ")}</div>
          {kind === "companies" && (
            <>
              <div><b>state</b> - one of: QLD, NSW, VIC, SA, WA, TAS, ACT, NT, NZ, PNG</div>
              <div><b>customer_tier</b> - whole number 1–6</div>
              <div><b>country</b> - Australia · New Zealand · Papua New Guinea · Other. Defaults to Australia.</div>
              <div><b>tier_reason_notes</b> - Free text (max 500 chars). Use this to describe the tier reason in your own words.</div>
              <div><b>current_customer</b> / <b>owns_equipment</b> - Yes or No</div>
              <div><b>type_of_contact</b> - Customer · Competitor · Partner</div>
              <div><b>product_segments</b> - semicolon-separated codes from: M (Mixer), D (Drills), B (Blovac), K (Kerbmaker), F (Fejmert), G (Geotechnical), N (Daikin), O (Other). Example: <code>M;D;G</code></div>
              <div><b>mixer_market_segment</b> (Specifics tab) - One of: <code>TOEM</code>, <code>CCOM</code>, <code>SPCL</code>, <code>Other</code>.</div>
              <div><b>mixer_history_need</b> (Customer history / need - Mixer) - Semicolon-separated. Allowed: <code>Maxi Bowl</code>, <code>Mini Bowl</code>, <code>Special</code>, <code>FBU</code>, <code>Spare Parts</code>, <code>Other</code>.</div>
              <div><b>drill_market_segment</b> (Specifics tab) - One of: <code>DCON</code> (Drilling Contractor), <code>LCC</code> (Large Civil Contractor), <code>MOEM</code> (Machine OEM), <code>HIRE</code> (Hire Company), <code>GEO</code> (Geotechnical).</div>
              <div><b>drill_history_need</b> (Products Customer Uses) - Semicolon-separated. Allowed: <code>CFA</code>, <code>Tooling</code>, <code>Wear Parts</code>, <code>Vibratory Hammer</code>, <code>Drill Rig</code>, <code>Impact Hammers</code>, <code>Geotechnical</code>, <code>Other</code>.</div>
              <div><b>drill_hire</b> (Products Customer Hires) - Semicolon-separated codes. Allowed: <code>HV</code> (Hire Vibro), <code>HI</code> (Hire Impact), <code>HD</code> (Hire Drill Rig), <code>HN</code> (Hire Never), <code>Other</code>.</div>
              <div><b>assigned_rep_email</b> - email of an existing active user (admin or sales). Leave blank to leave unassigned.</div>
              <div className="italic">Duplicate company names are skipped (not overwritten). Company name match is case-insensitive.</div>
            </>
          )}
          {kind === "contacts" && (
            <>
              <div><b>company_name</b> - must match a company already in the CRM exactly (case-insensitive). Import companies first.</div>
              <div><b>contact_title</b> - Mr · Mrs · Ms · Dr (optional)</div>
              <div><b>decision_maker</b> - Yes or No</div>
              <div><b>phone_office / phone_mobile</b> - any format; will be displayed as e.g. (+61) 412345678</div>
              <div><b>linkedin_url / facebook_url</b> - full profile URLs (https://…). Bare domains are auto-prefixed with https://.</div>
              <div><b>Summary on Customer</b> - free-form notes about the contact (hobbies, preferred contact time, etc.) - single editable field on the contact card.</div>
            </>
          )}
          {kind === "equipment" && (
            <>
              <div><b>company_name</b> - must match a company already in the CRM exactly (case-insensitive). Import companies first.</div>
              <div><b>date_of_manufacture</b> - MM/YYYY (e.g. 03/2021)</div>
              <div><b>type</b> - Mixer · Drill · Attachment · Spare Part · Other (free text)</div>
              <div><b>product_segment</b> - single-letter code: M · D · B · K · F · G · N · O (see Companies rules above)</div>
              <div><b>Cesco Serial Number</b> - Cesco&apos;s internal serial/reference for this machine.</div>
              <div className="italic">Duplicate serial numbers are allowed (two machines may share a serial).</div>
            </>
          )}
        </div>
      </div>

      <div className="cesco-card p-4 space-y-3">
        <div className="font-semibold">3. Upload</div>
        <input type="file" accept=".csv" onChange={e=>setFile(e.target.files?.[0] || null)}
               data-testid="import-file"/>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">When a row matches an existing record</div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" name="mode" value="skip" checked={mode === "skip"}
                   onChange={()=>setMode("skip")} data-testid="mode-skip" className="mt-1"/>
            <div>
              <div><b>Skip duplicates</b> <span className="text-gray-500">(safe default)</span></div>
              <div className="text-xs text-gray-500">Existing records aren&apos;t touched. Useful for first-time / additive imports.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" name="mode" value="upsert" checked={mode === "upsert"}
                   onChange={()=>setMode("upsert")} data-testid="mode-upsert" className="mt-1"/>
            <div>
              <div><b>Update existing</b> <span className="text-gray-500">(edit-in-Excel → re-upload)</span></div>
              <div className="text-xs text-gray-500">Matching records have their fields overwritten. <b>Empty cells are left alone</b> - they don&apos;t blank out existing data.</div>
              {kind === "companies" && <div className="text-xs text-gray-500">Match key: <b>company_name</b> (case-insensitive). Summary on Company appends a new dated entry if changed; history preserved.</div>}
              {kind === "contacts"  && <div className="text-xs text-gray-500">Match key: <b>direct_email</b> first, then <b>(company + first_name + last_name)</b>.</div>}
              {kind === "equipment" && <div className="text-xs text-gray-500">Match key: <b>Cesco Serial Number</b> first, then <b>serial_number</b>, then <b>(company + item + model)</b>.</div>}
            </div>
          </label>
        </div>
        <button className="cesco-btn-primary" disabled={!file || busy} onClick={upload}
                data-testid="import-upload">
          {busy ? "Uploading…" : (mode === "upsert" ? "Upload & import / update" : "Upload & import")}
        </button>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>

      {result && (
        <div className="cesco-card p-4 space-y-2" data-testid="import-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div className="rounded bg-gray-50 px-3 py-2"><div className="text-xs text-gray-500">Total rows</div><div className="font-semibold">{result.total}</div></div>
            <div className="rounded bg-green-50 px-3 py-2"><div className="text-xs text-green-700">Created</div><div className="font-semibold text-green-800">{result.imported || 0}</div></div>
            <div className="rounded bg-blue-50 px-3 py-2"><div className="text-xs text-blue-700">Updated</div><div className="font-semibold text-blue-800">{result.updated || 0}</div></div>
            <div className="rounded bg-amber-50 px-3 py-2"><div className="text-xs text-amber-700">Skipped</div><div className="font-semibold text-amber-800">{result.skipped || 0}</div></div>
          </div>
          <div className="text-sm text-red-700 mt-2"><b>Errors:</b> {result.errors?.length || 0}</div>
          {result.errors?.length > 0 && (
            <>
              <button className="cesco-btn-ghost text-sm flex items-center gap-2" onClick={downloadErrors}
                      data-testid="import-download-errors">
                <Download size={14}/> Download error report (.csv)
              </button>
              <div className="mt-2 max-h-64 overflow-y-auto text-sm">
                {result.errors.slice(0, 100).map((e, i) => (
                  <div key={`err-${e.row}-${i}`} className="py-1 border-b"><b>Row {e.row}:</b> {e.error}</div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
